import * as React from 'react'

import { SkillCategoryInterface, SkillPerformanceData } from '../interfaces';
import {
  DataTable,
  Spinner,
  helpIcon,
  Tooltip,
  DataTableChip,
} from '../../../../../Shared/index';
import useScaledTableColumnWidths from '../../../../../Shared/hooks/useScaledTableColumnWidths';
import { useStickyTableHeader } from '../../../../../Shared/hooks/useStickyTableHeader';
import { NONE, smartGroupToClassName, getSortableSmartGroup, formatTime, formatMomentumScore, formatTrend, formatPercentage, formatLastPracticed, getSortableMomentumScore, getSortableTrend, getSortablePercentage, getSortableTime, getSortableLastPracticed, getSortableExposure, getSmartGroup } from '../../shared/shared';

const helpIconImg = <img alt={helpIcon.alt} src={helpIcon.src} />

interface BySkillProps {
  containerRef: React.RefObject<HTMLElement>;
  momentumScoreGoal: number;
  data: SkillPerformanceData[] | null;
  selectedSkillCategory?: SkillCategoryInterface;
  studentId?: number | string;
  onSkillCategoryClick?: (skillId: number) => void;
}

const BySkill: React.FC<BySkillProps> = ({ 
  containerRef,
  data,
  momentumScoreGoal, 
  onSkillCategoryClick,
  selectedSkillCategory, 
  studentId,
}) => {
  const tableRef = React.useRef<HTMLTableElement>(null)
  const wrapperRef = React.useRef<HTMLDivElement>(null)

  const columnWidths = [
    { width: 300, minWidth: 170 }, // Skill Category/Sub-Category
    { width: 200, minWidth: 140 }, // Smart Group
    { width: 200, minWidth: 140 }, // Avg Momentum Score
    { width: 200, minWidth: 140 }, // Avg Momentum Score Trend
    { width: 200, minWidth: 116 }, // Avg Exposure
    { width: 200, minWidth: 140 }, // Avg Questions Answered
    { width: 200, minWidth: 116 }, // Avg Replay Rate
    { width: 200, minWidth: 116 }, // Avg Time Spent
    { width: 250, minWidth: 150 }, // Number of Students Practiced / Last Practiced
  ];

  const scaledWidths = useScaledTableColumnWidths({
    columnWidths,
    containerRef,
    maxTableWidth: 2000,
    minTableWidth: 1200,
    additionalWidthRestrictions: 99
  });

  useStickyTableHeader({
    wrapperRef,
    tableRef,
    enabled: true,
    dependencies: [data],
  });


  const formatStudentCount = (numberOfStudents: number, totalClassroomStudents: number) => {
    if (!numberOfStudents || !totalClassroomStudents) return '—';
    const percentage = Math.round((100.0 * numberOfStudents) / totalClassroomStudents);
    return `${numberOfStudents} (${percentage}%)`;
  };

  const handleSkillClick = React.useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    const skillIdStr = event.currentTarget.dataset.skillId;
    if (skillIdStr && onSkillCategoryClick) {
      onSkillCategoryClick(Number(skillIdStr));
    }
  }, [onSkillCategoryClick]);


  const selectedSkillCategoryOrSubCategoryText = selectedSkillCategory ? 'skill sub-category' : 'skill category'
  const isStudentSpecific = Boolean(studentId)

  const headers = [
    {
      width: scaledWidths['300w-170min'],
      ariaLabel: selectedSkillCategory ? 'Skill Sub-Category' : 'Skill Category',
      name: selectedSkillCategory ? 'Skill Sub-Category' : 'Skill Category',
      attribute: 'name',
      sortAttribute: 'nameForSort',
      isSortable: true,
      noTooltip: true
    },
    {
      width: scaledWidths['200w-140min'],
      ariaLabel: 'Smart Group',
      name: (
        <span className="text-and-tooltip-wrapper">
          <span>Smart Group</span>
          <Tooltip
            skipPositioning={true}
            tooltipText={
              isStudentSpecific
                ? `Dynamic groups based on how this student's performance for this ${selectedSkillCategoryOrSubCategoryText} compares against your goal of ${momentumScoreGoal}. These groups also consider the student's exposure to ensure the grouping is reliable. You can change this goal at any time by clicking the 'Change goal' button.`
                : `Dynamic groups based on how the class' average performance for this ${selectedSkillCategoryOrSubCategoryText} compares against your goal of ${momentumScoreGoal}. These groups also consider the average exposure to ensure the classification is reliable. You can change this goal at any time by clicking the 'Change goal' button.`
            }
            tooltipTriggerText={helpIconImg}
          />
        </span>
      ),
      attribute: 'smartGroup',
      sortAttribute: 'smartGroupForSort',
      isSortable: true
    },
    {
      width: scaledWidths['200w-140min'],
      ariaLabel: isStudentSpecific ? 'Momentum Score' : 'Avg Momentum Score',
      name: (
        <span className="text-and-tooltip-wrapper">
          <span>{isStudentSpecific ? 'Momentum Score' : 'Avg Momentum Score'}</span>
          <Tooltip
            skipPositioning={true}
            tooltipText={
              isStudentSpecific
                ? `A growth-focused score that reflects this student's recent improvements and consistent effort for this ${selectedSkillCategoryOrSubCategoryText}.`
                : `The average Momentum Score across all students for this ${selectedSkillCategoryOrSubCategoryText}.`
            }
            tooltipTriggerText={helpIconImg}
          />
        </span>
      ),
      attribute: 'avgMomentumScore',
      sortAttribute: 'avgMomentumScoreForSort',
      isSortable: true,
      rowSectionClassName: 'momentum-score'
    },
    {
      width: scaledWidths['200w-140min'],
      ariaLabel: isStudentSpecific ? 'Momentum Score Trend' : 'Avg Momentum Score Trend',
      name: (
        <span className="text-and-tooltip-wrapper">
          <span>{isStudentSpecific ? 'Momentum Score Trend' : 'Avg Momentum Score Trend'}</span>
          <Tooltip
            skipPositioning={true}
            tooltipText={
              isStudentSpecific
                ? `Analyzes recent changes in this student's Momentum Score to reveal their performance trajectory for this ${selectedSkillCategoryOrSubCategoryText}.`
                : `The average performance trajectory across all students for this ${selectedSkillCategoryOrSubCategoryText}.`
            }
            tooltipTriggerText={helpIconImg}
          />
        </span>
      ),
      attribute: 'avgTrend',
      sortAttribute: 'avgTrendForSort',
      isSortable: true,
      rowSectionClassName: 'momentum-score-trend'
    },
    {
      width: scaledWidths['200w-116min'],
      ariaLabel: isStudentSpecific ? 'Exposure' : 'Avg Exposure',
      name: (
        <span className="text-and-tooltip-wrapper">
          <span>{isStudentSpecific ? 'Exposure' : 'Avg Exposure'}</span>
          <Tooltip
            skipPositioning={true}
            tooltipText={
              isStudentSpecific
                ? `A measure of the practice this student has completed for this ${selectedSkillCategoryOrSubCategoryText}. Higher exposure increases the reliability of the Momentum Score.`
                : `The average amount of practice students have completed for this ${selectedSkillCategoryOrSubCategoryText}. Higher average exposure increases the reliability of the average Momentum Score.`
            }
            tooltipTriggerText={helpIconImg}
          />
        </span>
      ),
      attribute: 'avgExposure',
      sortAttribute: 'avgExposureForSort',
      isSortable: true
    },
    {
      width: scaledWidths['200w-140min'],
      ariaLabel: isStudentSpecific ? 'Questions Answered' : 'Avg Questions Answered',
      name: (
        <span className="text-and-tooltip-wrapper">
          <span>{isStudentSpecific ? 'Questions Answered' : 'Avg Questions Answered'}</span>
          <Tooltip
            skipPositioning={true}
            tooltipText={
              isStudentSpecific
                ? `The total number of questions this student has answered for this ${selectedSkillCategoryOrSubCategoryText}.`
                : `The average number of questions answered by students for this ${selectedSkillCategoryOrSubCategoryText}.`
            }
            tooltipTriggerText={helpIconImg}
          />
        </span>
      ),
      attribute: 'avgQuestionsAnswered',
      sortAttribute: 'avgQuestionsAnsweredForSort',
      isSortable: true
    },
    {
      width: scaledWidths['200w-116min'],
      ariaLabel: isStudentSpecific ? 'Replay Rate' : 'Avg Replay Rate',
      name: (
        <span className="text-and-tooltip-wrapper">
          <span>{isStudentSpecific ? 'Replay Rate' : 'Avg Replay Rate'}</span>
          <Tooltip
            skipPositioning={true}
            tooltipText={
              isStudentSpecific
                ? `Measures the frequency with which this student revisits questions for this ${selectedSkillCategoryOrSubCategoryText}. A high rate can signify both persistence and potential areas of difficulty.`
                : `The average frequency with which students revisit questions for this ${selectedSkillCategoryOrSubCategoryText}. A high rate can signify both persistence and potential areas of difficulty across the class.`
            }
            tooltipTriggerText={helpIconImg}
          />
        </span>
      ),
      attribute: 'avgReplayRate',
      sortAttribute: 'avgReplayRateForSort',
      isSortable: true
    },
    {
      width: scaledWidths['200w-116min'],
      ariaLabel: isStudentSpecific ? 'Time Spent' : 'Avg Time Spent',
      name: (
        <span className="text-and-tooltip-wrapper">
          <span>{isStudentSpecific ? 'Time Spent' : 'Avg Time Spent'}</span>
          <Tooltip
            skipPositioning={true}
            tooltipText={
              isStudentSpecific
                ? `The total time this student has spent answering questions for this ${selectedSkillCategoryOrSubCategoryText}.`
                : `The average time students have spent answering questions for this ${selectedSkillCategoryOrSubCategoryText}.`
            }
            tooltipTriggerText={helpIconImg}
          />
        </span>
      ),
      attribute: 'avgTimeSpent',
      sortAttribute: 'avgTimeSpentForSort',
      isSortable: true
    },
    ...(isStudentSpecific ? [{
      width: scaledWidths['250w-150min'],
      ariaLabel: 'Last Practiced',
      name: (
        <span className="text-and-tooltip-wrapper">
          <span>Last Practiced</span>
          <Tooltip
            skipPositioning={true}
            tooltipText={`The most recent date this student answered a question for this ${selectedSkillCategoryOrSubCategoryText}.`}
            tooltipTriggerText={helpIconImg}
          />
        </span>
      ),
      attribute: 'lastPracticed',
      sortAttribute: 'lastPracticedForSort',
      isSortable: true
    }] : [{
      width: scaledWidths['250w-150min'],
      ariaLabel: 'Number of Students Practiced',
      name: (
        <span className="text-and-tooltip-wrapper">
          <span>Number of Students Practiced</span>
          <Tooltip
            skipPositioning={true}
            tooltipText={`The total number of students who have answered at least one question for this ${selectedSkillCategoryOrSubCategoryText}, shown as a raw number and a percentage of the class.`}
            tooltipTriggerText={helpIconImg}
          />
        </span>
      ),
      attribute: 'numberOfStudents',
      sortAttribute: 'numberOfStudentsForSort',
      isSortable: true
    }])
  ];

  if (!data) { return <Spinner /> }

  const rows = data.map(row => {
    const {
      id,
      name,
      avg_ema,
      avg_trend,
      avg_exposure,
      avg_questions_answered,
      avg_replay_rate,
      avg_time_spent,
      last_practiced,
      number_of_students,
      total_classroom_students
    } = row
    const smartGroup = getSmartGroup(avg_exposure, avg_ema, avg_questions_answered, momentumScoreGoal)
    return {
      id: name,
      name: selectedSkillCategory ? name : (
        <button
          className="interactive-wrapper focus-on-light"
          data-skill-id={id}
          onClick={handleSkillClick}
          type="button"
        >
          <DataTableChip color='green' label={name} />
        </button>
      ),
      nameForSort: name,
      avgMomentumScore: formatMomentumScore(avg_ema, avg_exposure),
      avgMomentumScoreForSort: getSortableMomentumScore(avg_ema),
      avgTrend: formatTrend(avg_trend, avg_questions_answered),
      avgTrendForSort: getSortableTrend(avg_trend),
      avgExposure: avg_questions_answered === 0 ? NONE : avg_exposure,
      avgExposureForSort: getSortableExposure(avg_questions_answered === 0 ? NONE : avg_exposure),
      avgQuestionsAnswered: avg_questions_answered || '—',
      avgQuestionsAnsweredForSort: avg_questions_answered || 0,
      avgReplayRate: formatPercentage(avg_replay_rate, avg_questions_answered),
      avgReplayRateForSort: getSortablePercentage(avg_replay_rate, avg_questions_answered),
      avgTimeSpent: formatTime(avg_time_spent),
      avgTimeSpentForSort: getSortableTime(avg_time_spent),
      lastPracticed: formatLastPracticed(last_practiced),
      lastPracticedForSort: getSortableLastPracticed(last_practiced),
      numberOfStudents: formatStudentCount(number_of_students, total_classroom_students),
      numberOfStudentsForSort: number_of_students || 0,
      smartGroup: <span className={smartGroupToClassName[smartGroup]}>{smartGroup}</span>,
      smartGroupForSort: getSortableSmartGroup(smartGroup, avg_ema, name)
    }
  });

  return (
    <div className="by-skill">
      <div className="data-table-wrapper" ref={wrapperRef}>
        <DataTable
          defaultSortAttribute='smartGroupForSort'
          headers={headers}
          reportingFormat={true}
          rows={rows}
          showCheckboxes={false}
          tableRef={tableRef}
        />
      </div>
    </div>
  );
}

export default BySkill
